import type { ArticleContent } from "@/mocks/articles-shared";
import {
  IMG_GUIDE,
  IMG_REVIEW,
  IMG_SHIPPING,
  IMG_COUPON,
  IMG_DISCOUNT,
  IMG_WAREHOUSE,
  IMG_LEGIT,
  IMG_SAFETY,
  IMG_QC,
  IMG_SELLERS,
  IMG_PRODUCTS,
} from "@/mocks/articles-shared";

export const articlesFr: ArticleContent[] = [
  {
    slug: "usfans-spreadsheet-guide",
    title: "USFans Spreadsheet : le guide complet pour débutants",
    shortAnswer:
      "USFans Spreadsheet est un catalogue de produits organisé qui regroupe chaussures, sweats à capuche, t-shirts, maillots, accessoires et plus au même endroit, pour comparer les produits, consulter les notes des vendeurs et comprendre la livraison avant d'acheter.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "8 min de lecture",
    heroImage: IMG_GUIDE,
    toc: ["Qu'est-ce que USFans Spreadsheet ?", "Comment fonctionne le catalogue", "Les catégories de produits", "Comment trouver des produits", "Contrôle qualité (QC)", "Livraison, entrepôt et coûts", "Est-ce légitime et sûr ?"],
    sections: [
      { heading: "Qu'est-ce que USFans Spreadsheet ?", paragraphs: ["USFans Spreadsheet est un catalogue structuré qui rassemble vêtements de rue et produits pour fans dans une liste unique et lisible. Au lieu de cliquer sur des dizaines de pages, vous faites défiler une feuille organisée et voyez les produits groupés par type."] },
      { heading: "Comment fonctionne le catalogue", paragraphs: ["Chaque section se concentre sur un type de produit, avec le nom, une courte description et un lien vers le site principal. Cette mise en page prévisible aide les visiteurs réguliers à trouver vite ce qu'ils cherchent."] },
      { heading: "Les catégories de produits", paragraphs: ["Le catalogue couvre chaussures, sweats à capuche, t-shirts, vestes, pantalons, couvre-chefs, ensembles, sous-vêtements, maillots, accessoires, sacs, ceintures, parfums et une section générale. Une structure claire évite de chercher dans toute la boutique."] },
      { heading: "Comment trouver des produits", paragraphs: ["Ouvrez le menu des catégories ou appuyez sur un bouton de catégorie, puis parcourez les articles. Chaque entrée renvoie vers le site principal pour tous les détails et prix."] },
      { heading: "Contrôle qualité (QC)", paragraphs: ["Cherchez des photos claires, des notes de tailles cohérentes et des descriptions de la matière. Les articles avec des fiches détaillées sont plus faciles à évaluer."] },
      { heading: "Livraison, entrepôt et coûts", paragraphs: ["La livraison dépend de l'origine et de votre emplacement. Beaucoup de catalogues travaillent avec des entrepôts, ce qui influence le délai et le coût. Vérifiez toujours les détails sur le site principal avant de commander."] },
      { heading: "Est-ce légitime et sûr ?", paragraphs: ["La feuille est un catalogue informatif qui renvoie vers la boutique principale. Vérifiez la boutique, les options de paiement et évitez les sites qui imitent l'original."] },
    ],
    tableTitle: "Feuille de calcul vs recherche traditionnelle",
    table: {
      headers: ["Critère", "USFans Spreadsheet", "Recherche traditionnelle"],
      rows: [
        ["Découverte", "Organisée", "Dispersée"],
        ["Comparaison côte à côte", "Facile", "Difficile"],
        ["Convient aux débutants", "Élevé", "Moyen"],
        ["Trouver les nouveautés", "Rapide", "Plus lent"],
      ],
    },
    faq: [
      { q: "Est-ce gratuit de parcourir USFans Spreadsheet ?", a: "Oui, parcourir les catégories est simple. Les prix et le paiement se font sur le site principal." },
      { q: "Ai-je besoin d'un compte pour voir les produits ?", a: "Vous pouvez généralement parcourir sans compte. La création, si nécessaire, se fait sur le site principal." },
      { q: "À quelle fréquence la feuille est-elle mise à jour ?", a: "La fréquence varie. Pour les offres et prix actuels, consultez toujours le site principal." },
    ],
  },
  {
    slug: "usfans-review",
    title: "Avis USFans : ce que disent vraiment les acheteurs",
    shortAnswer:
      "Les avis USFans portent sur la qualité des produits, la fiabilité des vendeurs, la rapidité de livraison et le service client. Les lire dans ces domaines vous aide à juger si une offre vaut votre argent.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_REVIEW,
    toc: ["Pourquoi les avis comptent", "Ce que les acheteurs louent", "Les plaintes courantes", "Bien lire un avis"],
    sections: [
      { heading: "Pourquoi les avis comptent", paragraphs: ["Les avis sont l'un des moyens les plus rapides d'évaluer un produit et un vendeur avant d'acheter. Ils révèlent les tailles réelles, la qualité du tissu et la ressemblance avec les photos."] },
      { heading: "Ce que les acheteurs louent", paragraphs: ["Les avis positifs mentionnent souvent des photos fidèles, des coutures solides et des articles conformes à la description. Une livraison rapide et une bonne communication reviennent aussi souvent."] },
      { heading: "Les plaintes courantes", paragraphs: ["Les avis négatifs signalent souvent des tailles trop grandes ou petites, une livraison plus lente ou de légères différences de couleur. Rarement rédhibitoires, ils aident à fixer les attentes."] },
      { heading: "Bien lire un avis", paragraphs: ["Cherchez des tendances dans de nombreux avis plutôt que de vous fier à une seule opinion. Consultez d'abord les plus récents, car les lots et vendeurs changent."] },
    ],
    tableTitle: "Ce que révèlent les avis",
    table: {
      headers: ["Critère", "Ce qu'il faut chercher"],
      rows: [
        ["Photos du produit", "Fidèles à l'article"],
        ["Tailles", "Retours cohérents"],
        ["Livraison", "Claire et réaliste"],
      ],
    },
    faq: [
      { q: "Les avis USFans sont-ils fiables ?", a: "Ils sont un signal utile lus dans leur ensemble. Concentrez-vous sur les tendances de nombreux avis." },
      { q: "Où lire les avis USFans ?", a: "Ils se trouvent généralement sur les pages produit du site principal et dans les discussions communautaires." },
      { q: "Quoi ignorer dans les avis ?", a: "Ignorez les avis émotifs sans détail. Cherchez des commentaires concrets sur la coupe et la qualité." },
    ],
  },
  {
    slug: "usfans-shipping-cost",
    title: "Frais de livraison USFans : guide complet",
    shortAnswer:
      "Les frais de livraison USFans dépendent du poids, de la destination et de la méthode choisie. Les commandes internationales peuvent inclure des frais de douane confirmés au paiement.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_SHIPPING,
    toc: ["Ce qui influence le coût", "National vs international", "Entrepôt et délais", "Réduire les coûts"],
    sections: [
      { heading: "Ce qui influence le coût", paragraphs: ["Le coût dépend du poids, de la taille, de la destination et de la méthode choisie. Les commandes plus lourdes ou volumineuses coûtent généralement plus."] },
      { heading: "National vs international", paragraphs: ["La livraison nationale est souvent moins chère et plus rapide. L'international ajoute la distance, d'éventuelles douanes et des frais selon le pays."] },
      { heading: "Entrepôt et délais", paragraphs: ["Les articles depuis un entrepôt peuvent arriver plus vite que ceux envoyés séparément. Le délai dépend toujours de votre emplacement et du transporteur."] },
      { heading: "Réduire les coûts", paragraphs: ["Regroupez plusieurs articles en une commande si possible et comparez les méthodes au paiement. Le coût exact est toujours confirmé sur le site principal."] },
    ],
    tableTitle: "Facteurs de livraison",
    table: {
      headers: ["Facteur", "Impact sur le coût"],
      rows: [
        ["Poids et taille", "Plus élevé pour les gros articles"],
        ["Destination", "Plus élevé à l'international"],
        ["Méthode", "Les rapides coûtent plus"],
      ],
    },
    faq: [
      { q: "Combien coûte la livraison USFans ?", a: "Il n'y a pas de prix unique. Le coût dépend du poids, de la destination et de la méthode, confirmé au paiement." },
      { q: "USFans livre-t-il à l'international ?", a: "Cela dépend des options de la boutique. Vérifiez les détails de livraison sur le site principal pour votre pays." },
      { q: "Les frais de douane sont-ils inclus ?", a: "Ils peuvent s'appliquer aux commandes internationales et sont souvent en plus des frais de livraison." },
    ],
  },
  {
    slug: "usfans-coupon",
    title: "Coupon USFans : comment trouver de vraies économies",
    shortAnswer:
      "Les coupons et réductions USFans peuvent baisser le total, mais ils ne sont pas toujours disponibles. Le plus sûr est de chercher des économies valables dans les promotions et la newsletter du site principal.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "5 min de lecture",
    heroImage: IMG_COUPON,
    toc: ["D'où viennent les coupons", "Comment appliquer un coupon", "Éviter les faux codes", "Quand ça vaut le coup"],
    sections: [
      { heading: "D'où viennent les coupons", paragraphs: ["Les coupons viennent souvent des promotions de la boutique, des soldes saisonnières ou de sa newsletter. Les sites de codes tiers sont moins fiables."] },
      { heading: "Comment appliquer un coupon", paragraphs: ["Ajoutez des articles au panier puis saisissez le code dans le champ coupon au paiement. La remise s'applique avant de finaliser."] },
      { heading: "Éviter les faux codes", paragraphs: ["Méfiez-vous des codes de sites inconnus ou des réseaux sociaux. Restez sur la boutique officielle et ne partagez pas vos données de paiement pour une remise."] },
      { heading: "Quand ça vaut le coup", paragraphs: ["Un coupon vaut surtout pour les grosses commandes. Pour les petites, chercher un code peut ne pas valoir les quelques euros gagnés."] },
    ],
    tableTitle: "Sources de coupons",
    table: {
      headers: ["Source", "Fiabilité"],
      rows: [
        ["Boutique officielle", "Élevée"],
        ["Newsletter", "Élevée"],
        ["Sites de codes tiers", "Faible"],
      ],
    },
    faq: [
      { q: "Y a-t-il des coupons USFans valables ?", a: "Il peut y en avoir pendant les promotions. Vérifiez les offres du site principal pour les codes en vigueur." },
      { q: "Comment utiliser un code promo ?", a: "Saisissez-le dans le champ coupon au paiement avant de finaliser la commande." },
      { q: "Pourquoi mon code ne marche pas ?", a: "Il peut être expiré, exiger un minimum ou ne pas s'appliquer aux articles choisis." },
    ],
  },
  {
    slug: "usfans-discount-code",
    title: "Code promo USFans : ce qui marche et ce qu'il faut éviter",
    shortAnswer:
      "Les codes promo USFans peuvent réduire le total quand ils sont valables, mais beaucoup de codes en ligne sont expirés ou faux. Vérifiez toujours un code sur le site principal.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "5 min de lecture",
    heroImage: IMG_DISCOUNT,
    toc: ["Comment marchent les codes", "Pourquoi ils échouent", "Repérer une arnaque", "Économiser sans code"],
    sections: [
      { heading: "Comment marchent les codes", paragraphs: ["Un code promo est un court texte saisi au paiement. Il peut donner un pourcentage, un montant fixe ou la livraison gratuite."] },
      { heading: "Pourquoi ils échouent", paragraphs: ["Les codes expirent, exigent un minimum ou ne marchent que sur certains articles. S'il échoue, c'est souvent qu'il ne s'applique plus à votre panier."] },
      { heading: "Repérer une arnaque", paragraphs: ["Évitez les codes qui demandent de cliquer sur des liens suspects ou de saisir des données personnelles. Les remises légitimes s'appliquent au paiement officiel."] },
      { heading: "Économiser sans code", paragraphs: ["Soldes, lots et seuils de livraison gratuite économisent sans code. Comparez le total final plutôt que de courir après une seule remise."] },
    ],
    tableTitle: "Signaux d'alerte",
    table: {
      headers: ["Signal", "Signification"],
      rows: [
        ["Demande des données", "Arnaque probable"],
        ["Promet d'énormes remises", "Trop beau pour être vrai"],
        ["Champ de paiement officiel", "Lieu légitime"],
      ],
    },
    faq: [
      { q: "Où saisir le code promo USFans ?", a: "Saisissez-le dans le champ promo du paiement du site principal." },
      { q: "Les codes sont-ils garantis ?", a: "Non. Ils peuvent expirer ou avoir des conditions, vérifiez-les au paiement." },
      { q: "Puis-je cumuler plusieurs codes ?", a: "En général, un seul code s'applique par commande. Consultez les conditions de la boutique." },
    ],
  },
  {
    slug: "usfans-warehouse",
    title: "Entrepôt USFans : comment il affecte votre commande",
    shortAnswer:
      "Un entrepôt USFans peut accélérer le traitement et rendre le stock plus cohérent, mais il affecte aussi les options de livraison et l'origine de la commande.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_WAREHOUSE,
    toc: ["Ce que fait un entrepôt", "Impact sur la livraison", "Entrepôt vs vendeurs", "À vérifier avant de commander"],
    sections: [
      { heading: "Ce que fait un entrepôt", paragraphs: ["Un entrepôt stocke les produits en gros pour que les commandes soient préparées et expédiées plus vite. Cela signifie souvent un traitement plus rapide et un stock plus stable."] },
      { heading: "Impact sur la livraison", paragraphs: ["Les articles depuis un entrepôt peuvent avoir d'autres délais et coûts que ceux envoyés par un vendeur. L'origine affecte aussi les douanes."] },
      { heading: "Entrepôt vs vendeurs", paragraphs: ["Le stock d'entrepôt est souvent standardisé, tandis que les vendeurs individuels offrent plus de variété. Chacun a ses avantages."] },
      { heading: "À vérifier avant de commander", paragraphs: ["Confirmez l'origine de l'article et le délai estimé. Vérifiez les détails de livraison sur le site principal pour éviter les surprises."] },
    ],
    tableTitle: "Entrepôt vs envoi direct",
    table: {
      headers: ["Critère", "Entrepôt", "Vendeur"],
      rows: [
        ["Rapidité", "Souvent plus rapide", "Variable"],
        ["Cohérence", "Plus élevée", "Variable"],
        ["Origine", "Fixe", "Variable"],
      ],
    },
    faq: [
      { q: "USFans utilise-t-il un entrepôt ?", a: "Beaucoup de catalogues travaillent avec des entrepôts. Vérifiez les détails de livraison sur le site principal." },
      { q: "La livraison depuis un entrepôt est-elle plus rapide ?", a: "Elle est souvent plus rapide et cohérente, mais le délai dépend de votre emplacement." },
      { q: "Puis-je choisir l'origine ?", a: "L'origine est déterminée par la boutique, pas par l'acheteur. Vérifiez les options au paiement." },
    ],
  },
  {
    slug: "usfans-legit",
    title: "USFans est-il légitime ? Guide de sécurité",
    shortAnswer:
      "USFans peut être un endroit légitime pour acheter quand vous utilisez le site officiel, mais vérifiez toujours la boutique, les méthodes de paiement et les politiques, et évitez les faux sites.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_LEGIT,
    toc: ["Vérifier la boutique", "Signaux d'alerte", "Sécurité du paiement", "Ce qu'est ce site"],
    sections: [
      { heading: "Vérifier la boutique", paragraphs: ["Vérifiez que vous êtes sur le domaine officiel et cherchez des pages claires de contact et de politiques. Une boutique légitime offre des infos transparentes sur livraison et retours."] },
      { heading: "Signaux d'alerte", paragraphs: ["Méfiez-vous des domaines similaires, des demandes de paiement inhabituelles ou des prix trop beaux pour être vrais. Ce sont des signes habituels de sites non officiels."] },
      { heading: "Sécurité du paiement", paragraphs: ["Utilisez des méthodes sécurisées et ne partagez jamais vos données de paiement hors du paiement officiel. Évitez les virements à des tiers inconnus."] },
      { heading: "Ce qu'est ce site", paragraphs: ["Ce site est une ressource informative indépendante, pas la boutique. Vérifiez toujours les détails importants sur le site principal avant d'acheter."] },
    ],
    tableTitle: "Liste de vérification",
    table: {
      headers: ["Contrôle", "Que faire"],
      rows: [
        ["Domaine officiel", "Vérifiez l'URL"],
        ["Politiques claires", "Lisez livraison et retours"],
        ["Paiement sécurisé", "Méthodes de confiance"],
      ],
    },
    faq: [
      { q: "USFans est-il légitime ?", a: "Il peut l'être quand vous achetez via le site officiel. Vérifiez toujours la boutique et les méthodes de paiement." },
      { q: "Comment éviter les faux sites ?", a: "Utilisez uniquement le domaine officiel et méfiez-vous des URL similaires et demandes de paiement inhabituelles." },
      { q: "Ce site est-il la boutique ?", a: "Non. C'est une ressource informative indépendante, pas la boutique officielle." },
    ],
  },
  {
    slug: "usfans-safety",
    title: "Sécurité USFans : conseils pour acheter sans risque",
    shortAnswer:
      "Acheter en sécurité sur USFans signifie utiliser le site officiel, des méthodes de paiement sécurisées et vérifier les détails du vendeur et du produit avant de commander.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_SAFETY,
    toc: ["Utilisez le site officiel", "Protégez votre paiement", "Protégez vos données", "Connaître les risques de livraison"],
    sections: [
      { heading: "Utilisez le site officiel", paragraphs: ["Achetez toujours via le site officiel, pas via des liens de messages ou annonces inconnus. Les faux sites sont un moyen courant de tromperie."] },
      { heading: "Protégez votre paiement", paragraphs: ["Choisissez des méthodes sécurisées et traçables. Évitez les virements directs ou paiements par carte cadeau à des inconnus."] },
      { heading: "Protégez vos données", paragraphs: ["Ne partagez jamais mots de passe, données de carte complètes ou documents hors du paiement officiel. Les boutiques légitimes ne les demandent pas par message."] },
      { heading: "Connaître les risques de livraison", paragraphs: ["Les commandes internationales peuvent entraîner des douanes et des délais plus longs. Lisez les politiques de livraison et retours."] },
    ],
    tableTitle: "Bonnes pratiques",
    table: {
      headers: ["Domaine", "Bonne pratique"],
      rows: [
        ["Site", "Domaine officiel uniquement"],
        ["Paiement", "Méthodes sécurisées"],
        ["Données", "Jamais hors paiement"],
      ],
    },
    faq: [
      { q: "Acheter sur USFans est-il sûr ?", a: "Cela peut l'être avec le site officiel et des méthodes sécurisées. Méfiez-vous des liens non officiels." },
      { q: "Quelles méthodes de paiement sont les plus sûres ?", a: "Utilisez des méthodes sécurisées et traçables disponibles au paiement officiel." },
      { q: "Que ne jamais partager ?", a: "Jamais de mots de passe, données de carte complètes ou documents hors du paiement officiel." },
    ],
  },
  {
    slug: "usfans-qc",
    title: "Contrôle qualité USFans : vérifier avant d'acheter",
    shortAnswer:
      "Le contrôle qualité (QC) consiste à vérifier les photos, les tailles et les descriptions de matière avant de commander. De bonnes habitudes QC évitent les articles différents en vrai.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_QC,
    toc: ["Ce que signifie QC", "Ce qu'il faut chercher", "Lire les photos", "Quand poser des questions"],
    sections: [
      { heading: "Ce que signifie QC", paragraphs: ["Le contrôle qualité vérifie qu'un produit répond aux attentes avant l'expédition. Pour l'acheteur, c'est examiner les fiches avec soin avant de commander."] },
      { heading: "Ce qu'il faut chercher", paragraphs: ["Cherchez des photos claires et multiples, des tableaux de tailles détaillés et des descriptions de matière. Plus il y a de détails, plus c'est facile à évaluer."] },
      { heading: "Lire les photos", paragraphs: ["Comparez les photos avec la description. Si une fiche n'a qu'une photo ou une image très retouchée, traitez-la avec prudence."] },
      { heading: "Quand poser des questions", paragraphs: ["Si la taille, la matière ou la couleur n'est pas claire, cherchez plus d'infos ou comparez avec des articles similaires. Quelques minutes de vérification évitent les déceptions."] },
    ],
    tableTitle: "Liste de contrôle QC",
    table: {
      headers: ["Élément", "Que vérifier"],
      rows: [
        ["Photos", "Claires et multiples"],
        ["Tailles", "Tableau détaillé"],
        ["Matière", "Décrite clairement"],
      ],
    },
    faq: [
      { q: "Que signifie QC chez USFans ?", a: "QC signifie contrôle qualité, vérifier les produits par rapport aux attentes avant l'expédition." },
      { q: "Comment vérifier la qualité avant d'acheter ?", a: "Examinez photos, tableaux de tailles et descriptions de matière, et comparez avec des articles similaires." },
      { q: "Et si une fiche a peu de photos ?", a: "Traitez-la avec prudence et cherchez plus d'informations avant de commander." },
    ],
  },
  {
    slug: "usfans-sellers",
    title: "Vendeurs USFans : guide pour trouver des vendeurs fiables",
    shortAnswer:
      "Un vendeur fiable USFans a des fiches claires, des avis cohérents et des politiques transparentes. Vérifier ces signaux aide à éviter les vendeurs peu fiables.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_SELLERS,
    toc: ["Pourquoi les vendeurs comptent", "Signaux d'un bon vendeur", "Signaux d'alerte", "Comparer les vendeurs"],
    sections: [
      { heading: "Pourquoi les vendeurs comptent", paragraphs: ["Les vendeurs déterminent la qualité, la rapidité de livraison et la gestion des problèmes. Choisir un bon vendeur est aussi important que choisir le produit."] },
      { heading: "Signaux d'un bon vendeur", paragraphs: ["Cherchez des fiches détaillées, des avis positifs cohérents et des politiques claires de livraison et retour. Les bons vendeurs communiquent clairement."] },
      { heading: "Signaux d'alerte", paragraphs: ["Méfiez-vous des vendeurs aux fiches vagues, avis incohérents ou demandes de paiement inhabituelles. Ce sont des signes de faible fiabilité."] },
      { heading: "Comparer les vendeurs", paragraphs: ["Si plusieurs vendeurs offrent un article similaire, comparez photos, descriptions et avis. De petites différences en disent long sur la fiabilité."] },
    ],
    tableTitle: "Signaux du vendeur",
    table: {
      headers: ["Signal", "Bon", "Alerte"],
      rows: [
        ["Fiches", "Détaillées", "Vagues"],
        ["Avis", "Cohérents", "Incohérents"],
        ["Politiques", "Claires", "Peu claires"],
      ],
    },
    faq: [
      { q: "Comment trouver un bon vendeur USFans ?", a: "Cherchez des fiches détaillées, des avis cohérents et des politiques claires avant de commander." },
      { q: "Quels sont les signaux d'alerte ?", a: "Fiches vagues, avis incohérents et demandes de paiement inhabituelles sont des signes habituels." },
      { q: "Puis-je comparer les vendeurs ?", a: "Oui. Comparez photos, descriptions et avis quand plusieurs vendeurs offrent un article similaire." },
    ],
  },
  {
    slug: "usfans-products",
    title: "Produits USFans : ce que vous pouvez trouver",
    shortAnswer:
      "Les produits USFans couvrent les vêtements de rue et articles pour fans, comme chaussures, sweats à capuche, t-shirts, maillots, sacs et accessoires. Connaître la gamme aide à trouver plus vite.",
    author: "Équipe éditoriale USFans",
    updated: "Mis à jour en août 2026",
    readTime: "6 min de lecture",
    heroImage: IMG_PRODUCTS,
    toc: ["La gamme de produits", "Catégories populaires", "Comment ils sont listés", "Choisir le bon article"],
    sections: [
      { heading: "La gamme de produits", paragraphs: ["USFans couvre une large gamme de vêtements de rue et d'articles pour fans, des chaussures et vêtements aux accessoires et sacs. Le catalogue les regroupe pour naviguer vite."] },
      { heading: "Catégories populaires", paragraphs: ["Chaussures, sweats à capuche et t-shirts sont souvent les sections les plus populaires. Accessoires, sacs et maillots sont courants pour des cadeaux ou pièces précises."] },
      { heading: "Comment ils sont listés", paragraphs: ["Les produits sont groupés par catégorie, avec des liens vers le site principal pour tous les détails et prix. Cela rend la navigation prévisible."] },
      { heading: "Choisir le bon article", paragraphs: ["Décidez ce qu'il vous faut et allez directement à cette catégorie. Comparez quelques articles similaires avant de choisir pour correspondre à vos attentes."] },
    ],
    tableTitle: "Catégories populaires",
    table: {
      headers: ["Catégorie", "Usage typique"],
      rows: [
        ["Chaussures", "Usage quotidien"],
        ["Sweats et t-shirts", "Vêtements décontractés"],
        ["Accessoires et sacs", "Cadeaux et extras"],
      ],
    },
    faq: [
      { q: "Quels produits vend USFans ?", a: "Le catalogue couvre chaussures, vêtements, maillots, accessoires, sacs et plus, groupés par catégorie." },
      { q: "Quelles catégories sont les plus populaires ?", a: "Chaussures, sweats à capuche et t-shirts sont souvent les plus populaires." },
      { q: "Comment les produits sont-ils organisés ?", a: "Ils sont groupés par catégorie, avec des liens vers le site principal pour tous les détails." },
    ],
  },
];